import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { whopService } from '../../services/whopService';
import Button from '../../components/ui/Button';
import NavigationBar from '../../components/ui/NavigationBar';
import WaitlistModal from './components/WaitlistModal';
import { CreditCard, Check, Zap, Users, BarChart } from 'lucide-react';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);

  const isActive = profile?.subscription_status === 'active';
  const currentPlan = profile?.subscription_plan || 'free';

  const plans = [
    {
      id: 'pro',
      name: 'Pro',
      price: billingCycle === 'monthly' ? '$47' : '$499',
      period: billingCycle === 'monthly' ? 'per month' : 'per year',
      monthlyCost: billingCycle === 'annual' ? '$42/mo' : null,
      description: 'Unlock full training suite',
      features: [
        'All training modules',
        'Objection handling drills',
        'Script library access',
        'Micro-lessons & concepts',
        'Performance analytics',
        'Unlimited practice drills',
        'Leaderboards'
      ],
      cta: isActive && currentPlan === 'pro' ? 'Current Plan' : 'Upgrade to Pro',
      disabled: isActive && currentPlan === 'pro',
      popular: true,
      whopProductIdMonthly: 'plan_DBXVLNLTqBIYK',
      whopProductIdAnnual: 'plan_2nQAUoWggHLBk'
    },
    {
      id: 'elite',
      name: 'Elite',
      price: billingCycle === 'monthly' ? '$97' : '$999',
      period: billingCycle === 'monthly' ? 'per month' : 'per year',
      monthlyCost: billingCycle === 'annual' ? '$83/mo' : null,
      description: 'AI-powered coaching, analysis, and social-ready content',
      features: [
        'Everything in Pro',
        'AI Conversation Analyzer',
        'AI Roleplay scenarios',
        'Advanced feedback engine',
        'Priority AI processing',
        'Social-media driven UGC content kits'
      ],
      cta: isActive && currentPlan === 'elite' ? 'Current Plan' : 'Upgrade to Elite',
      disabled: isActive && currentPlan === 'elite',
      whopProductIdMonthly: 'plan_eXbZqIxnR13uz',
      whopProductIdAnnual: 'plan_auyU5uFXjcyIE'
    },
    {
      id: 'founder',
      name: 'Founder\'s Lifetime',
      price: '$10,000',
      period: 'one-time',
      description: 'Own your sales coaching forever',
      features: [
        'Everything in Elite',
        'Unlimited AI coaching credits',
        'Lifetime platform access',
        'Priority feature access',
        'VIP badge on profile',
        'Exclusive Discord community',
        'Early access to new tools',
        'Lifetime updates & support',
        'Limited to 30 lifetime members'
      ],
      cta: isActive && currentPlan === 'founder' ? 'Lifetime Member' : 'Join Waitlist',
      disabled: isActive && currentPlan === 'founder',
      premium: true,
      whopProductIdMonthly: 'REPLACE_WITH_FOUNDER_ID',
      whopProductIdAnnual: 'REPLACE_WITH_FOUNDER_ID'
    }
  ];

  const handleUpgrade = async (plan) => {
    // Show waitlist for Founder tier
    if (plan.id === 'founder') {
      setWaitlistModalOpen(true);
      return;
    }

    try {
      setLoading(true);
      
      // Select product ID based on billing cycle
      const productId = billingCycle === 'monthly' 
        ? plan.whopProductIdMonthly 
        : plan.whopProductIdAnnual;
      
      // Redirect to Whop product checkout
      window.location.href = `https://whop.com/checkout/${productId}?d2c=true`;
      
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      alert('Failed to upgrade subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <NavigationBar />
      <WaitlistModal isOpen={waitlistModalOpen} onClose={() => setWaitlistModalOpen(false)} />
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Unlock advanced training, analytics, and team features to accelerate your sales growth
          </p>
          
          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                billingCycle === 'annual' ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${
                  billingCycle === 'annual' ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>
              Annual
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs font-semibold">Save 12%</span>
            </span>
          </div>
        </div>

        {/* Current Subscription Status */}
        {isActive && (
          <div className="max-w-3xl mx-auto mb-8 bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-900">Active Subscription</h3>
                <p className="text-sm text-green-700">
                  You're currently on the <span className="font-bold capitalize">{currentPlan}</span> plan
                  {profile?.cancel_at_period_end && (
                    <span className="ml-2 text-amber-700">(Cancels at end of period)</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-xl border-2 ${
                plan.premium
                  ? 'border-amber-500 ring-4 ring-amber-100'
                  : plan.popular
                  ? 'border-blue-500 ring-4 ring-blue-100'
                  : 'border-gray-200'
              } p-6 flex flex-col hover:shadow-2xl transition-all duration-300`}
            >
              {/* Premium Badge */}
              {plan.premium && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-6 py-1.5 rounded-full shadow-lg">
                    👑 EXCLUSIVE
                  </span>
                </div>
              )}

              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-bold px-6 py-1.5 rounded-full shadow-lg">
                    ⭐ MOST POPULAR
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-6 text-center">
                <h3 className={`text-2xl font-bold mb-2 ${plan.premium ? 'bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4 min-h-[40px]">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className={`text-5xl font-bold ${plan.premium ? 'bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent' : 'bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent'}`}>
                    {plan.price}
                  </span>
                  <span className="text-gray-500 text-sm">/{plan.period}</span>
                </div>
              </div>

              {/* Features List */}
              <ul className="space-y-3 mb-6 flex-grow">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.premium ? 'text-amber-500' : 'text-green-500'}`} />
                    <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={() => handleUpgrade(plan)}
                disabled={plan.disabled || loading}
                className={`w-full py-3 font-semibold text-base rounded-xl transition-all ${
                  plan.premium
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl'
                    : plan.popular
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                    : plan.disabled
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-white border-2 border-gray-300 hover:border-blue-500 text-gray-900 hover:bg-blue-50'
                }`}
                variant={plan.popular || plan.premium ? 'default' : 'outline'}
              >
                {loading ? 'Loading...' : plan.cta}
              </Button>
            </div>
          ))}
        </div>

        {/* Features Comparison */}
        <div className="mt-12 max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            Everything You Need to Close More Deals
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            From AI-powered coaching to advanced analytics, get the tools top performers use
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <Zap className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">AI-Powered Training</h3>
              <p className="text-sm text-gray-600">
                Get real-time feedback from Lumo, our AI coach trained on thousands of successful sales calls
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <BarChart className="w-8 h-8 text-green-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Performance Analytics</h3>
              <p className="text-sm text-gray-600">
                Track your progress with detailed metrics on clarity, confidence, control, and closing ability
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <Zap className="w-8 h-8 text-purple-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Advanced AI Features</h3>
              <p className="text-sm text-gray-600">
                Access AI roleplay scenarios, conversation analysis, and personalized coaching feedback
              </p>
            </div>
          </div>
        </div>

        {/* FAQ or Support Link */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 text-sm">
            Questions about plans?{' '}
            <button
              onClick={() => navigate('/app/settings')}
              className="text-blue-600 hover:text-blue-700 underline font-medium"
            >
              Contact Support
            </button>
          </p>
        </div>
      </div></div>
  );
};

export default SubscriptionPage;
